import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useQuery } from "react-query";
// primereact
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
// components
import Modal from "../../../components/modal/Modal";
// services
import { getWhatsappConnection } from "../../../services/WhatsappConnection/getWhatsappConnection";

const ModalWhatsAppConnection = ({ visible, onHide, connectionId, onConnectionSuccess }) => {
  const [pollingInterval, setPollingInterval] = useState(10000);
  const [connectionSteps, setConnectionSteps] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const getQRCodeSrc = (base64String) => {
    if (!base64String) return "";
    
    if (base64String.startsWith("data:")) {
      return base64String;
    }
    
    return `data:image/png;base64,${base64String}`;
  };

  const {
    data: connectionData,
    isLoading,
    isError,
    refetch
  } = useQuery(
    ['whatsapp-connection', connectionId],
    () => getWhatsappConnection(connectionId),
    {
      enabled: Boolean(visible && connectionId),
      refetchInterval: isConnected ? false : pollingInterval,
      onSuccess: (data) => {
        if (data?.data?.is_connected && !isConnected) {
          setIsConnected(true);
          setConnectionSteps(3);
          toast.success("WhatsApp conectado com sucesso!");
          
          if (onConnectionSuccess) {
            onConnectionSuccess();
          }
          
          setTimeout(() => {
            onHide();
          }, 2000);
        } else if (data?.data?.qrcode && connectionSteps < 2) {
          setConnectionSteps(2);
        } else if (connectionSteps < 1) {
          setConnectionSteps(1);
        }
      },
      onError: (error) => {
        console.error("Erro ao verificar conexão:", error);
      }
    }
  );

  const connection = connectionData?.data;

  useEffect(() => {
    if (visible) {
      setConnectionSteps(0);
      setIsConnected(false);
      setImageLoading(true);
    }
  }, [visible, connectionId]);

  useEffect(() => {
    if (connection?.qrcode) {
      setImageLoading(false);
    }
  }, [connection?.qrcode]);

  const getStepTitle = () => {
    if (isConnected) return "Conectado com Sucesso!";
    if (connectionSteps >= 2 && connection?.qrcode) return "Escaneie o QR Code";
    if (connectionSteps >= 1) return "Aguardando QR Code...";
    return "Iniciando Conexão...";
  };

  const getStepDescription = () => {
    if (isConnected) return "Sua conexão WhatsApp está ativa e pronta para uso!";
    if (connectionSteps >= 2 && connection?.qrcode) return "Abra o WhatsApp no seu celular e escaneie o código QR para conectar.";
    if (connectionSteps >= 1) return "Aguarde enquanto geramos o código QR para conexão...";
    return "Configurando sua nova sessão do WhatsApp...";
  };

  const handleClose = () => {
    if (isConnected) {
      onHide();
    } else {
      toast.warning("Conexão ainda não foi estabelecida. Tem certeza que deseja fechar?");
      onHide();
    }
  };

  const handleRefreshQR = () => {
    refetch();
    toast.info("Atualizando QR Code...");
  };

  return (
    <Modal
      visible={visible}
      onHide={handleClose}
      dynamicTitle="Conectar WhatsApp"
      closable={!isLoading}
      style={{ width: "500px" }}
    >
      <div className="text-center px-4 py-3">
        {/* Header com gradiente WhatsApp */}
        <div 
          className="mb-4 p-4 rounded-4"
          style={{
            background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
            color: "white",
            position: "relative",
            overflow: "hidden"
          }}
        >
          <div 
            className="icon-container mx-auto mb-3"
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(10px)",
              border: "2px solid rgba(255,255,255,0.3)"
            }}
          >
            {isConnected ? (
              <i className="fa-solid fa-check" style={{ fontSize: "2.5rem", color: "white" }} />
            ) : (
              <i className="fa-brands fa-whatsapp" style={{ fontSize: "2.5rem", color: "white" }} />
            )}
          </div>
          <h3 className="mb-2 fw-bold">{getStepTitle()}</h3>
          <p className="mb-0 opacity-90">{getStepDescription()}</p>
        </div>

        {/* Loading State */}
        {isLoading && !connection && (
          <div className="my-4">
            <div className="d-flex justify-content-center mb-3">
              <div 
                className="spinner-border text-success" 
                style={{ width: "3rem", height: "3rem" }}
                role="status"
              >
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
            <p className="text-muted">Configurando conexão...</p>
          </div>
        )}

        {/* Aguardando QR Code */}
        {!isLoading && !connection?.qrcode && !isConnected && (
          <div className="my-4">
            <div className="d-flex justify-content-center mb-3">
              <div 
                className="spinner-border text-warning" 
                style={{ width: "3rem", height: "3rem" }}
                role="status"
              >
                <span className="visually-hidden">Aguardando QR Code...</span>
              </div>
            </div>
            <p className="text-muted">Gerando código QR...</p>
            {/* <Button
              label="Atualizar"
              icon="fa-solid fa-refresh"
              className="mt-2"
              onClick={handleRefreshQR}
              outlined
            /> */}
          </div>
        )}

        {/* QR Code Display */}
        {connection?.qrcode && !isConnected && (
          <div className="my-4">
            <div 
              className="qr-code-container mx-auto mb-3 p-3 bg-white rounded-4 shadow-sm"
              style={{ 
                maxWidth: "280px",
                border: "3px solid #25D366"
              }}
            >
              <img 
                src={getQRCodeSrc(connection.qrcode)}
                alt="QR Code WhatsApp" 
                style={{ 
                  width: "100%", 
                  height: "auto",
                  display: imageLoading ? "none" : "block"
                }}
                onLoad={() => setImageLoading(false)}
                onError={(e) => {
                  console.error("Erro ao carregar QR Code:", e);
                  setImageLoading(false);
                  toast.error("Erro ao carregar QR Code. Tente atualizar.");
                }}
              />
              {imageLoading && (
                <div className="text-center py-4">
                  <i className="fa-solid fa-spinner fa-spin me-2"></i>
                  Carregando QR Code...
                </div>
              )}
            </div>
            
            {/* <div className="alert alert-info border-0 rounded-4 mb-3" style={{ fontSize: "14px" }}>
              <i className="fa-solid fa-info-circle me-2"></i>
              <strong>Como conectar:</strong>
              <ol className="mb-0 mt-2 text-start" style={{ paddingLeft: "20px" }}>
                <li>Abra o WhatsApp no seu celular</li>
                <li>Toque nos três pontos (menu)</li>
                <li>Selecione "Dispositivos conectados"</li>
                <li>Toque em "Conectar dispositivo"</li>
                <li>Escaneie este código QR</li>
              </ol>
            </div> */}
          </div>
        )}

        {/* Success State */}
        {isConnected && (
          <div className="my-4">
            <div 
              className="alert alert-success border-0 rounded-4 mb-3"
              style={{
                background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                color: "white"
              }}
            >
              <i className="fa-solid fa-check-circle me-2"></i>
              <strong>Conexão estabelecida!</strong>
              <p className="mb-0 mt-2">
                Seu WhatsApp está conectado e pronto para uso. Esta janela se fechará automaticamente.
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="my-4">
            <div className="alert alert-danger border-0 rounded-4 mb-3">
              <i className="fa-solid fa-exclamation-triangle me-2"></i>
              <strong>Erro na conexão</strong>
              <p className="mb-0 mt-2">
                Não foi possível estabelecer a conexão. Tente novamente.
              </p>
            </div>
            <Button
              label="Tentar Novamente"
              icon="fa-solid fa-refresh"
              onClick={() => refetch()}
              severity="danger"
            />
          </div>
        )}

        {/* Footer Buttons */}
        {!isConnected && (
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button
              label="Cancelar"
              icon="fa-solid fa-times"
              onClick={handleClose}
              outlined
              severity="secondary"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ModalWhatsAppConnection;
